import { ProductLevel, TradeDirection, TradeFlow } from "@atlas-int/data/types";
import { groups } from 'd3';
import { TradeValueScaledByYAxisOption } from './constants';
import { TradeValues, YearRangeForProductClass } from './types';
import { createProductLookupIndex, createYearLookupIndex } from "@atlas-int/data";
import { fillMissingYearObservations } from "@atlas-int/data";
import type { ProductMetadatum, DeflatorDatum, LocationYearDatum } from "@atlas-int/data/types";
import type { TradeDataItem } from "@atlas-int/data/types";

/** Transformed product data point for a year */
interface TransformedProductYearItem extends Partial<TradeValues> {
  year: number;
  productId: string;
  nameEn: string | undefined;
  topLevelParent: string | undefined;
  productsData: TradeDataItem[];
}

/**
 * Computes scaled trade values for different Y-axis scaling options.
 * Uses the shared computeTotalSumByTradeFlow from Utils.
 */
import { computeTotalSumByTradeFlow } from "@atlas-int/data";

const computeScaledTradeValuesLocal = ({
  productsData,
  deflatorForYearMatchingMetadata,
  populationForYearMatchingMetadata,
  tradeFlow,
}: {
  productsData: TradeDataItem[];
  deflatorForYearMatchingMetadata: DeflatorDatum | undefined;
  populationForYearMatchingMetadata: LocationYearDatum | undefined;
  tradeFlow: TradeFlow;
}): TradeValues => {
  const tradeValues: TradeValues = {
    [TradeValueScaledByYAxisOption.Current]: { exportValue: 0, importValue: 0 },
    [TradeValueScaledByYAxisOption.Constant]: { exportValue: 0, importValue: 0 },
    [TradeValueScaledByYAxisOption.PerCapita]: { exportValue: 0, importValue: 0 },
    [TradeValueScaledByYAxisOption.PerCapitaConstant]: { exportValue: 0, importValue: 0 },
  };

  if (!productsData || productsData.length === 0) {
    return tradeValues;
  }

  const sumExportValue = computeTotalSumByTradeFlow({
    data: productsData,
    tradeFlow,
    tradeDirection: TradeDirection.Exports,
  }) ?? 0;

  const sumImportValue = computeTotalSumByTradeFlow({
    data: productsData,
    tradeFlow,
    tradeDirection: TradeDirection.Imports,
  }) ?? 0;

  tradeValues[TradeValueScaledByYAxisOption.Current] = {
    exportValue: sumExportValue,
    importValue: sumImportValue,
  };

  const deflator = deflatorForYearMatchingMetadata?.deflator;
  const population = populationForYearMatchingMetadata?.population;

  tradeValues[TradeValueScaledByYAxisOption.Constant] = deflator
    ? { exportValue: sumExportValue / deflator, importValue: sumImportValue / deflator }
    : { exportValue: sumExportValue, importValue: sumImportValue };

  tradeValues[TradeValueScaledByYAxisOption.PerCapita] = population
    ? { exportValue: sumExportValue / population, importValue: sumImportValue / population }
    : { exportValue: 0, importValue: 0 };

  tradeValues[TradeValueScaledByYAxisOption.PerCapitaConstant] = (deflator && population)
    ? { exportValue: sumExportValue / (deflator * population), importValue: sumImportValue / (deflator * population) }
    : { exportValue: 0, importValue: 0 };

  return tradeValues;
};

/**
 * Transforms product data for visualization
 * Main optimization: Use indexed lookups and reduce iterations
 */
const transformProducts = ({
  productLevel,
  untransformedData,
  products,
  yAxisScalingOption: _yAxisScalingOption,
  deflators,
  locationYearPopulations,
  yearRangeForProductClass,
  tradeFlow,
}: {
  productLevel: number | undefined;
  untransformedData: TradeDataItem[];
  products: ProductMetadatum[];
  yAxisScalingOption: string;
  deflators?: DeflatorDatum[];
  locationYearPopulations?: LocationYearDatum[];
  yearRangeForProductClass: YearRangeForProductClass;
  tradeFlow: TradeFlow;
}): TransformedProductYearItem[] | undefined => {
  if (untransformedData.length === 0) {
    return undefined;
  }

  // Step 6: Use shared fillMissingYearObservations
  const data = fillMissingYearObservations<TradeDataItem>({
    data: untransformedData,
    groupKey: 'productId',
    yearRange: yearRangeForProductClass,
  });

  // Use shared utilities for indexed lookups (O(1) access)
  const productLookup = createProductLookupIndex(products);
  const deflatorsLookup = createYearLookupIndex(deflators || []);
  const locationYearPopulationsLookup = createYearLookupIndex(locationYearPopulations || []);

  const flatGroupedByProductAndYear: TransformedProductYearItem[] = [];

  // Group data once
  const groupedData = groups(
    data,
    (d: TradeDataItem) => d.productId,
    (d: TradeDataItem) => d.year
  );

  groupedData.forEach(([productId, yearsData]) => {
    const productMetadata = productLookup.get(productId) as ProductMetadatum | undefined;
    const nameEn = productMetadata?.nameShortEn;
    const topLevelParent =
      productLevel !== ProductLevel.ProductSection
        ? productMetadata?.topParent?.productId
        : undefined;

    yearsData.forEach(([year, productsData]) => {
      const deflatorForYear = deflatorsLookup.get(year);
      const populationForYear = locationYearPopulationsLookup.get(year);

      const tradeValues = computeScaledTradeValuesLocal({
        productsData,
        tradeFlow,
        deflatorForYearMatchingMetadata: deflatorForYear,
        populationForYearMatchingMetadata: populationForYear,
      });

      flatGroupedByProductAndYear.push({
        year,
        productId,
        nameEn,
        topLevelParent,
        productsData,
        ...tradeValues,
      });
    });
  });

  return flatGroupedByProductAndYear;
};

export default transformProducts;
